import { Breakpoint, ButtonType, Typography, UiComponentSize } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { ShoppingCart } from 'app/api/@generated/backend';

import { ExtraUnitSelect } from 'app/components/extra-unit-select';
import { ImagesSlider } from 'app/components/images-slider';
import { i18n } from 'app/i18n';
import { HouseType } from 'app/pages/order/model/house-type';
import { OrderStep } from 'app/pages/order/redux/model/order-step';
import { OrderService } from 'app/pages/order/services/order-service';
import { wifiMeshUnitAllImages } from 'app/resources/images';
import { I18nUtils } from 'app/utils/i18n-utils';
import { Tab } from 'app/components/tabs';
import { TabProps } from 'app/components/tabs/tab';
import { ShoppingCartUtils } from 'app/utils/shopping-cart-utils';
import { CurrencyUtils } from 'app/utils/currency-utils';
import { FreeUnitInstructions, UpgradeUnitInstructions } from 'app/components/connection-instructions';
import { selectPartner, selectSelectedAmountOfUnitsToPurchase } from 'app/pages/order/redux/order-selector';
import { useGoToStep } from 'app/hooks/use-go-to-step';
import { pushAddToCartEvent } from 'app/data-layers/events/push-add-to-cart-event';

import {
    StyledContainer,
    StyledContentContainer,
    StyledCustomerDebtAlert,
    StyledDescription,
    StyledHomeGatewayUpgradeAlert,
    StyledMonthlyPrice,
    StyledNavigationButton,
    StyledNote,
    StyledPriceTitle,
    StyledTitle,
    StyledTotalPrice,
} from './product-details-tab.styled';

interface OwnProps {
    houseType: HouseType;
    customerHasDebt: boolean;
    extraUnitIsNeeded: boolean;
    shoppingCart: ShoppingCart;
}

type Props = OwnProps & TabProps;

export const ProductDetailsTab: FunctionComponent<Props> = (props) => {
    const {
        customerHasDebt,
        extraUnitIsNeeded,
        houseType,
        shoppingCart,
        shoppingCart: { homeGatewayUpgrade, isUpgradeUnitAdded, freeUnit, establishmentCharge },
        ...rest
    } = props;
    const amountOfUnitsToPurchase = useSelector(selectSelectedAmountOfUnitsToPurchase);
    const selectedWifiOption = ShoppingCartUtils.getSelectedWifiOption(shoppingCart, amountOfUnitsToPurchase);
    const partner = useSelector(selectPartner);
    const goToStep = useGoToStep();

    const handleButtonClick = (): void => {
        pushAddToCartEvent(shoppingCart, houseType, extraUnitIsNeeded, partner);
        goToStep(OrderStep.SUMMARY);
    };

    return (
        <Tab {...rest}>
            <StyledContainer>
                <ImagesSlider images={wifiMeshUnitAllImages} />
                <StyledTitle variant="headline2" component="h1">
                    {I18nUtils.message(i18n.page.productDetails.title, {
                        amount: OrderService.getNumberOfWifiUnitsByHouseType(houseType),
                    })}
                </StyledTitle>
                <StyledContentContainer>
                    <StyledDescription variant="paragraph1" component="p" maxBreakpoint={Breakpoint.TABLET}>
                        {i18n.page.productDetails.description[houseType]}
                    </StyledDescription>
                    {isUpgradeUnitAdded && <UpgradeUnitInstructions houseType={houseType} />}
                    {!!freeUnit && !isUpgradeUnitAdded && <FreeUnitInstructions houseType={houseType} />}
                    <ExtraUnitSelect shoppingCart={shoppingCart} extraUnitAdded={extraUnitIsNeeded} />
                </StyledContentContainer>
                <StyledMonthlyPrice variant="headline4" component="div" maxBreakpoint={Breakpoint.TABLET}>
                    {CurrencyUtils.formatPriceWithMonthlySuffix(selectedWifiOption.recurringCharge.price)}
                </StyledMonthlyPrice>
                <StyledTotalPrice>
                    <StyledPriceTitle variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                        {i18n.page.productDetails.firstYearPrice}
                    </StyledPriceTitle>
                    <Typography variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                        {CurrencyUtils.formatPriceWithSuffix(selectedWifiOption.totalFirstYearPrice)}
                    </Typography>
                </StyledTotalPrice>
                {!!establishmentCharge && (
                    <StyledTotalPrice>
                        <StyledPriceTitle variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                            {i18n.page.productDetails.establishmentPrice}
                        </StyledPriceTitle>
                        <Typography variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                            {CurrencyUtils.formatPriceWithSuffix(establishmentCharge.price)}
                        </Typography>
                    </StyledTotalPrice>
                )}
                {customerHasDebt && <StyledCustomerDebtAlert />}
                {!!homeGatewayUpgrade && !customerHasDebt && <StyledHomeGatewayUpgradeAlert />}
                <StyledNavigationButton
                    buttonType={ButtonType.PRIMARY_A}
                    uiComponentSize={UiComponentSize.LARGE}
                    onClick={handleButtonClick}
                    disabled={customerHasDebt}
                >
                    {i18n.page.productDetails.goToSummary}
                </StyledNavigationButton>
                <StyledNote variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                    {i18n.page.productDetails.returnNote}
                </StyledNote>
                <StyledNote variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
                    {i18n.page.productDetails.compatibilityNote}
                </StyledNote>
            </StyledContainer>
        </Tab>
    );
};
